/* API client — every API call in the admin goes through this.

   Why a wrapper, not raw fetch:
   - credentials:"include" must be set on every request for cookies to flow
   - 401s from `at` cookie expiry need to silently call /auth/refresh and
     retry exactly once — without that, every page load that hits an API
     after 15min throws the user back to /login
   - JSON content-type is set automatically when body is an object
   - Network errors become structured ApiError objects, not raw fetch
     TypeError exceptions

   On 401 retry: a single in-flight refresh is shared across all concurrent
   401s (so 4 parallel requests hitting an expired token don't trigger 4
   refreshes — they all wait on one promise). */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/v1";

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let refreshPromise = null;

async function doRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/* Nothing bounded how long a request could take. A socket that opens and
   then stalls -- a flaky mobile connection, a Neon compute waking from
   suspend -- left the promise pending forever, and AuthContext waits on
   /auth/me before deciding between login and dashboard. The result was a
   blank screen with no error and no login form: force-close and try again,
   sometimes twice.

   45s is generous for uploads; anything conversational answers in well under
   a second. AbortError surfaces as a normal rejection, so AuthContext's
   existing catch treats it as "not logged in" and shows the login screen. */
const REQUEST_TIMEOUT_MS = 45_000;

async function rawFetch(path, opts = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers = { ...(opts.headers || {}) };
  let body = opts.body;
  if (body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob)) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(body);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, {
      ...opts, headers, body,
      credentials: "include",
      signal: opts.signal ?? controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function parseResponse(res) {
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const msg = data?.message || data?.error || res.statusText || "Request failed";
    throw new ApiError(Array.isArray(msg) ? msg.join(", ") : msg, res.status, data);
  }
  return data;
}

/* The main API client. Use as:
     await api.get("/users")
     await api.post("/auth/login", { email, password })
     await api.patch(`/users/${id}`, { role: "admin" })

   On 401 (and the call isn't already a refresh / login / logout):
   - attempt a single shared /auth/refresh
   - on success, retry the original request once
   - on failure, throw — caller decides what to do (AuthContext bounces to /login) */
async function request(method, path, body, opts = {}) {
  const isAuthCriticalPath = path.startsWith("/auth/refresh")
    || path.startsWith("/auth/login")
    || path.startsWith("/auth/logout");

  const res = await rawFetch(path, { method, body, ...opts });
  if (res.status === 401 && !isAuthCriticalPath && !opts._retried) {
    const refreshed = await doRefresh();
    if (refreshed) {
      return request(method, path, body, { ...opts, _retried: true });
    }
  }
  return parseResponse(res);
}

export const api = {
  get: (path, opts) => request("GET", path, undefined, opts),
  post: (path, body, opts) => request("POST", path, body, opts),
  patch: (path, body, opts) => request("PATCH", path, body, opts),
  put: (path, body, opts) => request("PUT", path, body, opts),
  delete: (path, opts) => request("DELETE", path, undefined, opts),
  base: API_BASE,
};
